// Funding › Partner funds (the ABL ledger): what partners have with QOR (available, held by loads in flight, owed for
// advances), each partner's fund, what they owe and collecting it (Receivables), the partner fund changes waiting for
// a second system administrator, and every movement of every partner fund.
import { shortDate } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { fundsApi, type Approval, type FundsHealth } from '../api';
import { ApprovalsTable } from '../components/Approvals';
import type { FundingTabProps } from './Funding';
import { amountOf, cents, entryLabel, providerLabel } from './fundingLabels';
import { ReceivablesPanel } from './ReceivablesPanel';

const HISTORY_KINDS = [
  ['', 'Every movement', undefined],
  ['deposits', 'Deposits', ['deposit']],
  ['withdrawals', 'Withdrawals', ['withdrawal']],
  ['loads', 'Card loads', ['load', 'load_release', 'unload', 'reversal']],
  ['advances', 'Advances and paybacks', ['advance', 'advance_repayment']],
  ['write-offs', 'Write-offs', ['write_off', 'write_off_recovery']],
  ['corrections', 'Corrections', ['adjustment']],
] as const;

/** The partners' money with QOR, one line of figures per currency. */
function Totals({ partners }: { partners: FundsHealth['partners'] }) {
  const currencies = [...new Set(partners.map((p) => p.currency).filter((c): c is string => c !== null))];
  if (currencies.length === 0) return null;
  return (
    <>
      {currencies.map((cur) => {
        const of = partners.filter((p) => p.currency === cur);
        const sum = (pick: (p: FundsHealth['partners'][number]) => string) =>
          amountOf(of.reduce((total, p) => total + cents(pick(p)), 0));
        const overdue = of.filter((p) => p.overdue).length;
        return (
          <section className="qc-stat-row" key={cur} aria-label={`Partner funds in ${cur}`}>
            <div className="qc-stat-tile qc-stat-tile--success">
              <span className="qc-stat-label">Partners can load</span>
              <strong className="qc-stat-value">{sum((p) => p.available)}</strong>
              <span className="qc-stat-trend">
                {cur} · {of.length} fund{of.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="qc-stat-tile">
              <span className="qc-stat-label">Held by loads in flight</span>
              <strong className="qc-stat-value">{sum((p) => p.held)}</strong>
              <span className="qc-stat-trend">{cur}</span>
            </div>
            <div className="qc-stat-tile qc-stat-tile--purple">
              <span className="qc-stat-label">Partners owe QOR</span>
              <strong className="qc-stat-value">{sum((p) => p.advanceOutstanding)}</strong>
              <span className="qc-stat-trend">{cur} · advances not paid back</span>
            </div>
            <div className="qc-stat-tile qc-stat-tile--red">
              <span className="qc-stat-label">Overdue partners</span>
              <strong className="qc-stat-value">{overdue}</strong>
              <span className="qc-stat-trend">
                {of.filter((p) => p.frozen).length} frozen · {of.filter((p) => p.low).length} low
              </span>
            </div>
          </section>
        );
      })}
    </>
  );
}

function History({ partners }: { partners: FundsHealth['partners'] }) {
  const [programId, setProgramId] = useState('');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const kinds = HISTORY_KINDS.find(([key]) => key === kind)?.[2];
  const entries = useQuery({
    queryKey: ['partner-entries', programId, kind, page],
    queryFn: () => fundsApi.partnerEntries({ programId: programId || undefined, kinds }, page),
  });
  const names = [...new Map(partners.map((p) => [p.programId, p.name])).entries()];

  return (
    <div className="qc-panel" style={{ marginTop: 20 }}>
      <div className="qc-panel-title">
        <h3>History</h3>
        <span>Every movement of every partner fund, newest first.</span>
      </div>
      <div className="qc-cardholder-filters" style={{ marginBottom: 12 }}>
        <label>
          <span>Partner</span>
          <select
            value={programId}
            onChange={(e) => {
              setProgramId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Every partner</option>
            {names.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>What</span>
          <select
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setPage(1);
            }}
          >
            {HISTORY_KINDS.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {!entries.data || entries.data.items.length === 0 ? (
        <p className="qc-empty">No movements{programId || kind ? ' match' : ' yet'}.</p>
      ) : (
        <>
          <table className="qc-plain-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Partner</th>
                <th>What</th>
                <th>Amount</th>
                <th>Balance after</th>
                <th>Reference / note</th>
              </tr>
            </thead>
            <tbody>
              {entries.data.items.map((e) => (
                <tr key={e.id}>
                  <td>{shortDate(e.createdAt)}</td>
                  <td>
                    {e.programId ? (
                      <Link to={`/partners/${e.programId}?tab=funding`}>{e.programName ?? 'Partner'}</Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {entryLabel(e.kind)}
                    {e.advance !== '0.00' && (
                      <>
                        <br />
                        <small>owed {e.advance}</small>
                      </>
                    )}
                  </td>
                  <td>
                    {e.amount} <small>{e.currency}</small>
                  </td>
                  <td>{e.balanceAfter}</td>
                  <td>
                    {[e.reference, e.memo, e.dueAt && `due ${e.dueAt.slice(0, 10)}`].filter(Boolean).join(' · ') ||
                      (e.loadId ? 'Card load' : '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={entries.data.pageSize} total={entries.data.total} onPage={setPage} />
        </>
      )}
    </div>
  );
}

export function Pager({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  return (
    <div className="qc-pagination">
      <span>
        Page {page} · {total} total
      </span>
      <div className="qc-pagination-links">
        {page > 1 && (
          <button type="button" className="qc-button-ghost" onClick={() => onPage(page - 1)}>
            Previous
          </button>
        )}
        {page * pageSize < total && (
          <button type="button" className="qc-button-ghost" onClick={() => onPage(page + 1)}>
            Next
          </button>
        )}
      </div>
    </div>
  );
}

export function FundingPartnersTab({
  busy,
  done,
  decide,
  health: h,
  pending,
}: FundingTabProps & { health: FundsHealth | null; pending: Approval[] | null }) {
  const partners = h?.partners ?? [];
  return (
    <>
      <Totals partners={partners} />

      <div className="qc-panel">
        <div className="qc-panel-title">
          <h3>Partners</h3>
          <span>
            {h && h.unknownLoads.count > 0 ? (
              <>
                {h.unknownLoads.count} load(s) without an answer from the processor hold money:{' '}
                <Link to="/loads?status=unknown">resolve them</Link>.
              </>
            ) : (
              'Prefunded partners and every fund that holds money. Open one to ask for a deposit, a withdrawal or an advance.'
            )}
          </span>
        </div>
        {partners.length === 0 ? (
          <p className="qc-empty">No partner is prefunded yet (Partners › a partner › Funding).</p>
        ) : (
          <table className="qc-plain-table">
            <thead>
              <tr>
                <th>Partner</th>
                <th>Available</th>
                <th>Held</th>
                <th>Owes</th>
                <th>Next due</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr key={`${p.programId}:${p.currency ?? ''}`}>
                  <td>
                    <Link to={`/partners/${p.programId}?tab=funding`}>{p.name}</Link>
                    <br />
                    <small>
                      {providerLabel(p.provider)} {p.currency ?? ''}
                    </small>
                  </td>
                  <td>{p.available}</td>
                  <td>{p.held}</td>
                  <td>{p.advanceOutstanding}</td>
                  <td>{p.nextAdvanceDueAt ? shortDate(p.nextAdvanceDueAt) : '—'}</td>
                  <td>
                    {!p.enabled && <span className="qc-badge qc-badge-muted">Not prefunded</span>}{' '}
                    {p.frozen && <span className="qc-badge qc-badge-danger">Frozen</span>}{' '}
                    {p.overdue && <span className="qc-badge qc-badge-danger">Overdue</span>}{' '}
                    {p.low && <span className="qc-badge qc-badge-warning">Low</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>Waiting for approval</h3>
          <span>Deposits, withdrawals, advances, paybacks, corrections and write-offs of partner funds.</span>
        </div>
        {pending && (
          <ApprovalsTable
            items={pending}
            busy={busy}
            showProgram
            onDecide={(a, ok) => void decide(a, ok)}
            onChanged={(ok, message) => void done(ok, message)}
          />
        )}
      </div>

      <ReceivablesPanel onDone={(ok, message) => void done(ok, message)} />

      <History partners={partners} />
    </>
  );
}
